import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import routesfe from '../Config/RoutesFE';
import '../Styles/TermsOfService.css';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="terms-of-service-page">
      {/* Header Navbar */}
      <header className="policy-navbar">
        <div className="navbar-container">
          <div className="navbar-logo" onClick={() => navigate('/')}>
            <img src="/Images/noteforge-logo.png" alt="NoteForge Logo" className="nav-logo-img" />
            <span className="nav-logo-text">NoteForge</span>
          </div>
        </div>
      </header>

      <div className="terms-of-service-card">
        <div className="terms-of-service-header">
          <h1>Terms of Service</h1>
          <div className="header-divider"></div>
          <div className="header-meta">
            <div className="effective-date">
              <span className="date-label">Effective</span>
              <span className="date-value">January 22, 2026</span>
            </div>
            <div className="see-also">
              <span>See also:</span>
              <button onClick={() => navigate('/privacy-policy')} className="see-also-btn">Privacy Policy</button>
            </div>
          </div>
        </div>
        <div className="terms-of-service-content">

          <div className="terms-intro">
            <p>
              These Terms of Service ("Terms") form a binding legal agreement between you ("you," "your," or "User") and Noteforge LLC, doing business as NoteForge ("NoteForge," "we," "us," or "our"), governing your access to and use of the NoteForge AI-powered note-taking and productivity service, including our mobile applications for Android and iOS, website at <a href="https://noteforge.ai" target="_blank" rel="noopener noreferrer">noteforge.ai</a>, and any related features, tools, or content (collectively, the "Service").
            </p>
            <p>
              By accessing or using the Service, you agree to be bound by these Terms and our Privacy Policy (available at <a href="https://noteforge.ai/privacy-policy" target="_blank" rel="noopener noreferrer">https://noteforge.ai/privacy-policy</a>). If you do not agree to these Terms, you must not access or use the Service.
            </p>
            <p>
              If you are accessing the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms, and "you" and "your" will refer to that organization.
            </p>
          </div>

          <div className="terms-section">
            <h2>1. Service Overview and Eligibility</h2>
            <p>
              The Service is an AI-powered platform designed to help users capture, transcribe, and organize spoken conversations, such as meetings, lectures, or interviews, transforming them into searchable, structured, and shareable notes.
            </p>
            <p>
              The Service is offered as a subscription-based service. Access to the Service is provided through paid plans only, and users may be required to pay applicable fees to use the Service (the "Paid Service"). At this time, we do not offer a free version of the Service.
            </p>

            <h3>Eligibility</h3>
            <p>You must be at least 13 years old to use the Service. By accessing or using the Service, you represent and warrant that:</p>
            <ul>
              <li>You are at least 13 years old;</li>
              <li>You have not previously been suspended or removed from the Service; and</li>
              <li>Your registration for and use of the Service complies with all applicable laws and regulations.</li>
            </ul>
            <p>
              The Service is intended for use by individuals, professionals, educational users, and organizations of varying sizes, including but not limited to individual users, small teams and workspaces, professional users, and business and enterprise organizations. NoteForge currently offers multiple subscription plans, including Basic Workspace, Professional Workspace, and Business or Enterprise Workspace plans.
            </p>
          </div>

          <div className="terms-section">
            <h2>2. Account Registration and Management</h2>
            <p>
              To access most features of the Service, you must register for an account. When you register, you may be required to provide accurate and complete information about yourself, such as your name, email address, phone number, or other contact information. You agree to keep this information up to date.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized access or use.
            </p>
            <p>
              Currently, all users register and authenticate individually using their own email addresses, and accounts are treated as individual user accounts. At this stage, we do not provide centralized administrative control by organizations. However, for organizational or business use cases, we may support "Authorized Users" in the future. In such cases, the organization will be responsible for managing, authenticating, and controlling access for its Authorized Users, and you agree that the organization may have access to your User Content and usage data.
            </p>
            <p>
              Accounts registered using corporate or organizational email domains are currently treated as individual user accounts. However, if an organization establishes an account with us or asserts ownership or control over an associated email domain, we reserve the right to migrate such accounts, restrict access, or terminate accounts as required by applicable law or organizational policies.
            </p>
          </div>

          <div className="terms-section">
            <h2>3. Payment and Subscription Terms</h2>
            <p>
              NoteForge is offered exclusively as a subscription-based paid service. Available subscription plans may include Basic, Professional, and Business or Enterprise plans, as described on our website at <a href="https://noteforge.ai" target="_blank" rel="noopener noreferrer">noteforge.ai</a>.
            </p>
            <p>
              Certain features of the Service require payment of applicable fees in U.S. Dollars (USD). Before purchasing a subscription, you will have the opportunity to review and accept the fees that will be charged.
            </p>
            <p>
              For mobile users, subscriptions must currently be purchased via our website. A pop-up in the mobile app will direct you to the website for subscription. In the future, we may enable payments through Google Play or Apple App Store.
            </p>
            <p>
              All fees are non-refundable, except as required by applicable law. We do not provide refunds or credits for partial subscription periods, unused features, or cancellations.
            </p>
            <p>At this time:</p>
            <ul>
              <li>Subscriptions do not automatically renew;</li>
              <li>Users are responsible for renewing their subscription at the end of each billing period if they wish to continue using the Service; and</li>
              <li>If a subscription is not renewed, access to paid features may expire at the end of the applicable subscription period.</li>
            </ul>
            <p>
              We may change pricing or introduce new fees at any time, with notice provided via the Service or email. Current plan details and pricing are available at <a href="https://noteforge.ai" target="_blank" rel="noopener noreferrer">noteforge.ai</a>.
            </p>

            <h3>Delinquent Accounts</h3>
            <p>
              We may suspend or terminate access to the Service, including paid features, for any account with fees that remain unpaid. If you access the Service through an organization or workspace and the organization fails to pay amounts due, we may suspend or terminate your access without notice. In addition to unpaid subscription fees, we may charge fees associated with chargebacks, collections, or other costs incurred in recovering overdue amounts, to the extent permitted by law.
            </p>
          </div>

          <div className="terms-section">
            <h2>4. User Content and Intellectual Property</h2>

            <h3>User Content Ownership and License</h3>
            <p>
              You retain ownership of all content you submit, upload, record, or generate through the Service ("User Content"). By using the Service, you grant NoteForge a worldwide, non-exclusive, royalty-free, transferable, and sublicensable license to host, store, process, reproduce, modify, and display User Content solely for the purpose of operating, improving, and providing the Service.
            </p>

            <h3>Audio Recordings and Consent</h3>
            <p>
              If you use the Service to record audio, you are solely responsible for obtaining all necessary consents from all participants prior to recording. You represent and warrant that your use of the Service complies with all applicable laws, including recording and privacy laws. NoteForge is not responsible for any unauthorized recordings made using the Service.
            </p>

            <h3>Usage Data and Service Improvement</h3>
            <p>
              We may collect and use aggregated and anonymized usage data (such as feature usage, performance metrics, and interaction patterns) to operate, analyze, and improve the Service, including improving our machine learning models. Such data does not identify individual users or include identifiable User Content.
            </p>

            <h3>NoteForge Intellectual Property</h3>
            <p>
              The Service, including all software, designs, text, graphics, and other materials, is owned by NoteForge or its licensors and protected by intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to access and use the Service for your personal or internal business purposes, subject to these Terms. You may not reproduce, modify, reverse engineer, or commercially exploit the Service.
            </p>
          </div>

          <div className="terms-section">
            <h2>5. Prohibited Conduct and Termination</h2>

            <h3>Prohibited Conduct</h3>
            <p>You agree not to, and not to permit any third party to:</p>
            <ul>
              <li>Use the Service for any unlawful, fraudulent, or unauthorized purpose;</li>
              <li>Violate any applicable law or regulation, including privacy, recording, or intellectual property laws;</li>
              <li>Upload, record, or share content that you do not have the legal right to use;</li>
              <li>Interfere with or disrupt the integrity or performance of the Service;</li>
              <li>Attempt to gain unauthorized access to the Service, accounts, systems, or networks;</li>
              <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Service;</li>
              <li>Use the Service to harass, abuse, threaten, or harm others;</li>
              <li>Circumvent or attempt to circumvent any usage limits, security features, or payment obligations; or</li>
              <li>Use the Service in a manner that could damage, disable, overburden, or impair our systems.</li>
            </ul>

            <h3>Termination</h3>
            <p>
              We may suspend or terminate your access to the Service, in whole or in part, at any time and for any reason or no reason, with or without notice, including if we believe you have violated these Terms or applicable law.
            </p>
            <p>
              You may terminate your account at any time by discontinuing use of the Service or by contacting us at <a href="mailto:support@noteforge.ai">support@noteforge.ai</a>.
            </p>
            <p>
              Upon termination, your right to access and use the Service will immediately cease. Any provisions that by their nature should survive termination will survive, including provisions relating to intellectual property, disclaimers, limitation of liability, indemnity, and governing law.
            </p>
          </div>

          <div className="terms-section">
            <h2>6. Liability and Dispute Resolution</h2>

            <h3>Disclaimer of Warranties</h3>
            <p>
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. To the maximum extent permitted by law, NoteForge disclaims all warranties of any kind, whether express, implied, or statutory, including warranties of merchantability, fitness for a particular purpose, accuracy, availability, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure.
            </p>

            <h3>Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, NoteForge shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of data, profits, revenue, or business, arising out of or related to your use of the Service. In no event shall NoteForge's total liability exceed the amount paid by you to NoteForge in the twelve (12) months preceding the event giving rise to the claim.
            </p>

            <h3>Indemnity</h3>
            <p>
              You agree to indemnify, defend, and hold harmless NoteForge, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising from your use of the Service, violation of these Terms, or infringement of any third-party rights.
            </p>

            <h3>Dispute Resolution and Arbitration</h3>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved by binding, individual arbitration, rather than in court, administered by the American Arbitration Association (AAA) in accordance with its Consumer Arbitration Rules. You and NoteForge waive any right to a trial by jury or to participate in a class action or representative proceeding. The arbitration shall be conducted in English. You may opt out of this arbitration provision by sending written notice to us at <a href="mailto:support@noteforge.ai">support@noteforge.ai</a> within 30 days of your first access to the Service.
            </p>

            <h3>Governing Law and Jurisdiction</h3>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law principles. For any disputes not subject to arbitration, you agree to the exclusive jurisdiction of the state and federal courts located in California, and you consent to the personal jurisdiction of such courts.
            </p>
          </div>

          <div className="terms-section">
            <h2>7. Privacy and Data Protection</h2>
            <p>
              We process your Personal Information in accordance with our Privacy Policy, which is incorporated into these Terms by reference.
            </p>

            <h3>International Data Protection (GDPR / UK GDPR)</h3>
            <p>
              NoteForge processes Personal Information in accordance with applicable data protection laws, including the EU General Data Protection Regulation ("GDPR") and the UK GDPR, where applicable. At this time, the Service is offered primarily to individual users and does not include enterprise-specific data processing arrangements. Where NoteForge acts as a data processor on behalf of an organization, a Data Processing Addendum (DPA) will be made available upon written request to <a href="mailto:support@noteforge.ai">support@noteforge.ai</a> to support compliance with applicable data protection laws.
            </p>

            <h3>U.S. State Privacy Rights</h3>
            <p>
              NoteForge complies with applicable U.S. state privacy laws, including the California Consumer Privacy Act ("CCPA"), as amended by the California Privacy Rights Act ("CPRA"), and similar privacy laws in other U.S. states. Subject to applicable law, users may have the right to request access to, correction of, or deletion of their Personal Information. NoteForge does not sell or share Personal Information for cross-context behavioral advertising. Requests to exercise privacy rights may be submitted by contacting us at <a href="mailto:support@noteforge.ai">support@noteforge.ai</a>.
            </p>
          </div>

          <div className="terms-section">
            <h2>8. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time to reflect changes in our practices or legal requirements. We will post the updated Terms on our website and indicate the effective date. Your continued use of the Service after the effective date constitutes your acceptance of the updated Terms. If we make material changes, we will provide notice via email or through the Service.
            </p>
          </div>

          <div className="terms-section">
            <h2>9. Miscellaneous</h2>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and NoteForge regarding the Service and supersede any prior agreements.
            </p>
            <p>
              If any provision of these Terms is held invalid or unenforceable, the remaining provisions will remain in full force and effect.
            </p>
            <p>
              You may not assign or transfer these Terms without our prior written consent. We may assign or transfer these Terms without restriction.
            </p>
            <p>
              Notices under these Terms may be sent electronically to the email address associated with your account.
            </p>
            <p>
              We are not responsible for any failure to perform due to causes beyond our reasonable control, such as acts of God, war, or natural disasters.
            </p>
            <p>
              For questions about these Terms, contact us at <a href="mailto:support@noteforge.ai">support@noteforge.ai</a>.
            </p>
          </div>

          <div className="terms-section">
            <h2>Apple-Specific Terms (for iOS Users)</h2>
            <p>
              If you access the Service via our iOS app, these Terms are between you and NoteForge, not Apple Inc. ("Apple"). Apple has no obligation to provide maintenance or support for the Service. In the event of any failure of the Service to conform to applicable warranties, you may notify Apple, and Apple will refund the purchase price (if any). Apple has no other warranty obligations. Apple is not responsible for addressing claims related to the Service, including product liability, consumer protection, or intellectual property infringement claims.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;